import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useWarehouseStore } from '@/stores/warehouseStore';
import { getAllMachines, type MachineResponse } from '@/api/machine.api';
import { getAllWorkCenters, type WorkCenterResponse } from '@/api/workcenter.api';
import { getAllWorkOrders, type WorkOrderResponse } from '@/api/workorder.api';
import { useToast } from '@/contexts/ToastContext';
import { getAllProductionOrders, type ProductionOrderResponse } from '@/api/productionOrder.api';
import { getAllProductionEntries, type ProductionEntryResponse } from '@/api/productionentry.api';
import { getAllDowntimeEntries, type DowntimeEntryResponse } from '@/api/downtime.api';
import { getAllOperators, type OperatorResponse } from '@/api/operators.api';
import { getAllShifts, type ShiftResponse } from '@/api/shift.api';
import OEEDashboard from './components/OEEDashboard';
import OverviewDashboard from './components/OverviewDashboard';
import QualityDashboard from './components/QualityDashboard';
import { toLocalDate } from '@/utils/format';

// Shop floor rows that are activity logs, not production output
const NON_PRODUCTION_STAGES = ['MATERIAL_ISSUE', 'Material Issue', 'DOWNTIME', 'Downtime', 'QC_INSPECTION', 'FINAL_COMPLETION'];

export default function LiveDashboardPage() {
  const navigate = useNavigate();
  const { selectedWarehouseId } = useWarehouseStore();

  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState<'overview' | 'oee' | 'quality'>(
    'overview',
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);

  /* Dynamic Data States - Pure DB Data */
  const [machines, setMachines] = useState<any[]>([]);
  const [workCenters, setWorkCenters] = useState<any[]>([]);
  const [workOrders, setWorkOrders] = useState<any[]>([]);
  const [productionOrders, setProductionOrders] = useState<any[]>([]);
  const [productionEntries, setProductionEntries] = useState<any[]>([]);
  const [downtimeEntries, setDowntimeEntries] = useState<any[]>([]);
  const [operators, setOperators] = useState<any[]>([]);
  const [shifts, setShifts] = useState<any[]>([]);

  const toast = useToast();
  // The page refreshes on a timer: a failing load is reported once, until it loads again
  const woErrorShown = useRef(false);

  /* ─── Fetch All Live Shop Floor Data ─── */
  const loadLiveData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [
        mcRes,
        wcRes,
        woRes,
        poRes,
        peRes,
        dtRes,
        opRes,
        sfRes,
      ] = await Promise.allSettled([
        getAllMachines(selectedWarehouseId || undefined),
        getAllWorkCenters(selectedWarehouseId || undefined),
        getAllWorkOrders(selectedWarehouseId),
        getAllProductionOrders({ warehouseId: selectedWarehouseId || undefined }),
        getAllProductionEntries(selectedWarehouseId || undefined),
        getAllDowntimeEntries(selectedWarehouseId || undefined),
        // Operators and shifts without a warehouse are shared, so load them all
        getAllOperators(),
        getAllShifts(),
      ]);

      if (woRes.status === 'rejected') {
        if (!woErrorShown.current) toast.error(woRes.reason instanceof Error ? woRes.reason.message : 'Could not load work orders');
        woErrorShown.current = true;
      } else {
        woErrorShown.current = false;
      }

      const rows = <T,>(r: PromiseSettledResult<any>): T[] =>
        r.status === 'fulfilled' && r.value?.success && Array.isArray(r.value.data) ? r.value.data : [];

      // 1. Machines
      setMachines(
        rows<MachineResponse>(mcRes)
          .filter((m) => m.is_active !== false)
          .map((m) => ({
            id: m.id,
            name: m.name,
            workCenterId: m.work_center_id || '',
            workCenterName: m.work_center_name || '',
            status: m.status,
            capacityPerHour: Number(m.capacity_per_hour) || 0,
          })),
      );

      // 2. Work Centers
      setWorkCenters(
        rows<WorkCenterResponse>(wcRes).map((wc) => ({
          id: wc.id,
          name: wc.name,
          type: wc.type,
        })),
      );

      // 3. Work Orders
      const mappedWorkOrders = rows<WorkOrderResponse>(woRes).map((wo) => ({
        id: wo.id,
        woNumber: wo.wo_number || '',
        productionOrderId: wo.production_order_id || '',
        productionOrderNumber: wo.production_order_number || '',
        stageNumber: Number(wo.stage_number) || 0,
        stageName: wo.stage_name || '',
        workCenterId: wo.work_center_id || '',
        workCenterName: wo.work_center_name || '',
        machineId: wo.machine_id || null,
        machineName: wo.machine_name || null,
        operatorId: wo.operator_id || null,
        operatorName: wo.operator_name || null,
        plannedQty: Number(wo.planned_qty) || 0,
        completedQty: Number(wo.completed_qty) || 0,
        rejectedQty: Number(wo.rejected_qty) || 0,
        status: wo.status,
        plannedStartDate: wo.planned_start_date || '',
        plannedEndDate: wo.planned_end_date || '',
        plannedTimeMinutes: Number(wo.planned_time_minutes) || 0,
        actualStartDate: wo.actual_start_date || null,
        actualEndDate: wo.actual_end_date || null,
        itemName: (wo as any).item_name || '',
        assignedAt: (wo as any).assigned_at || null,
        plannedStartAt: (wo as any).planned_start_at || null,
        plannedEndAt: (wo as any).planned_end_at || null,
      }));
      setWorkOrders(mappedWorkOrders);
      const machineByWorkOrder = new Map(mappedWorkOrders.map((wo) => [wo.id, wo.machineId]));

      // 4. Production Orders
      setProductionOrders(
        rows<ProductionOrderResponse>(poRes).map((po) => ({
          id: po.id,
          poNumber: po.po_number || '',
          productName: po.item_name || '',
          status: po.status,
        })),
      );

      // 5. Production Entries — real output rows only (start/complete logs, material issues and
      //    QC logs carry no production). The machine falls back to the work order's machine.
      setProductionEntries(
        rows<ProductionEntryResponse>(peRes)
          .filter((e) => !NON_PRODUCTION_STAGES.includes(e.stage_name || ''))
          .filter((e) => Number(e.produced_qty) > 0 || Number(e.rejected_qty) > 0)
          .map((e) => ({
            id: e.id,
            entryNumber: e.entry_number || '',
            workOrderId: e.work_order_id || '',
            workOrderNumber: e.work_order_number || '',
            productionOrderId: e.production_order_id || '',
            stageName: e.stage_name || '',
            machineId: e.machine_id || machineByWorkOrder.get(e.work_order_id || '') || null,
            operatorId: e.operator_id || '',
            operatorName: e.operator_name || '',
            shiftId: e.shift_id || null,
            date: toLocalDate(e.date),
            producedQty: Number(e.produced_qty) || 0,
            rejectedQty: Number(e.rejected_qty) || 0,
            unit: e.unit || '',
            startTime: e.start_time || '',
            endTime: e.end_time || '',
            actualTimeMinutes: Number(e.actual_time_minutes) || 0,
            createdAt: e.created_at,
          })),
      );

      // 6. Downtime Entries
      setDowntimeEntries(
        rows<DowntimeEntryResponse>(dtRes).map((d) => ({
          id: d.id,
          entryNumber: d.entry_number || '',
          machineId: d.machine_id || '',
          machineName: d.machine_name || '',
          downtimeCodeName: d.downtime_code_name || '',
          category: d.category || '',
          shiftId: d.shift_id || null,
          startTime: d.start_time || '',
          endTime: d.end_time || '',
          durationMinutes: d.duration_minutes ? Number(d.duration_minutes) : 0,
          isResolved: d.is_resolved ?? false,
          date: toLocalDate(d.date),
          createdAt: d.created_at,
        })),
      );

      // 7. Operators
      setOperators(
        rows<OperatorResponse>(opRes).map((op) => ({
          id: op.id,
          name: op.name,
          employeeCode: op.employee_code || '',
          skill: op.skill,
          shiftId: op.shift_id || null,
        })),
      );

      // 8. Shifts
      setShifts(
        rows<ShiftResponse>(sfRes).map((sh) => ({
          id: sh.id,
          name: sh.name,
          startTime: sh.start_time || '',
          endTime: sh.end_time || '',
          breakMinutes: Number(sh.break_minutes) || 0,
          workingDays: sh.working_days || [],
          isActive: sh.is_active !== false,
        })),
      );

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to load shop floor live metrics:', err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedWarehouseId, toast]);

  /* Load on mount & when warehouse changes */
  useEffect(() => {
    loadLiveData();
  }, [loadLiveData]);

  /* Auto refresh every 30 seconds */
  useEffect(() => {
    const interval = setInterval(() => {
      loadLiveData();
      setRefreshKey((k) => k + 1);
    }, 30000);
    return () => clearInterval(interval);
  }, [loadLiveData]);

  const handleRefreshNow = () => {
    loadLiveData();
    setRefreshKey((k) => k + 1);
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-[#f8fafc] py-4 px-4 md:px-6">
        <div className="max-w-[1600px] mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-[#1e293b]">
                  Live Production Dashboard
                </h1>
                {isLoading && (
                  <span className="inline-flex items-center gap-1 text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full animate-pulse">
                    <i className="ri-loader-4-line animate-spin text-xs" /> Syncing...
                  </span>
                )}
              </div>
              <p className="text-xs text-[#64748b] mt-0.5">
                Auto-refreshes every 30 seconds &bull; Last updated:{' '}
                {lastUpdated.toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefreshNow}
                disabled={isLoading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-[#475569] text-xs font-medium border border-slate-200 hover:bg-slate-50 transition-colors disabled:opacity-50 cursor-pointer"
              >
                <i className={`ri-refresh-line ${isLoading ? 'animate-spin' : ''}`} />
                Refresh Now
              </button>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                Live
              </span>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1 mb-3 w-fit">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'overview'
                  ? 'bg-[#1e293b] text-white'
                  : 'text-[#475569] hover:bg-slate-50'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('oee')}
              className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'oee'
                  ? 'bg-[#1e293b] text-white'
                  : 'text-[#475569] hover:bg-slate-50'
              }`}
            >
              OEE
            </button>
            <button
              onClick={() => setActiveTab('quality')}
              className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'quality'
                  ? 'bg-[#1e293b] text-white'
                  : 'text-[#475569] hover:bg-slate-50'
              }`}
            >
              Quality
            </button>
          </div>

          {activeTab === 'overview' && (
            <OverviewDashboard
              machines={machines}
              workCenters={workCenters}
              workOrders={workOrders}
              productionOrders={productionOrders}
              productionEntries={productionEntries}
              downtimeEntries={downtimeEntries}
              operators={operators}
              shifts={shifts}
            />
          )}

          {activeTab === 'oee' && (
            <OEEDashboard
              machines={machines}
              workCenters={workCenters}
              shifts={shifts}
              productionEntries={productionEntries}
              downtimeEntries={downtimeEntries}
            />
          )}

          {activeTab === 'quality' && (
            <QualityDashboard
              refreshKey={refreshKey}
              warehouseId={selectedWarehouseId || undefined}
              productionEntries={productionEntries}
              workOrders={workOrders}
            />
          )}
        </div>
      </div>
    </AppLayout>
  );
}
